import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { useTrackFirstView } from '@ifixit/analytics/hooks/use-track-first-view';
import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { Duration } from '@ifixit/utils';
import { useCacheableQuery } from '@ifixit/utils-react/hooks';
import { Badge } from '@ifixit/ui/components/badge';
import { Button } from '@ifixit/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ifixit/ui/components/dialog';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { _js } from '@ifixit/utils/localize';
import { getGrowthbookSync } from 'Shared/features';
import initializeComponent from 'Shared/react-initialize';
import { Recaptcha } from 'Shared/recaptcha';
import { _jsr } from 'Shared/translations_react';
import {
   useEffect,
   useMemo,
   useRef,
   useState,
   useSyncExternalStore,
   type ReactNode,
   type RefObject,
   type SubmitEvent,
} from 'react';
import { createPortal } from 'react-dom';
import * as z from 'zod';
import { useLatestDefined } from 'Shared/hooks/use-latest-defined';

const MAX_DISTANCE_MILES = 75;
const NEAREST_SHOP_LIMIT = 3;
const EVENT_CATEGORY = 'Repair Shop Locator';

const GUIDE_POSITION_FLAG = 'pro-lead-guide-position';

const ProShopSchema = z.object({
   id: z.number(),
   name: z.string(),
   address: z.string(),
   addressUnit: z.string().nullable(),
   city: z.string(),
   state: z.string(),
   postcode: z.string(),
   country: z.string(),
   latitude: z.number(),
   longitude: z.number(),
   distanceMiles: z.number(),
   affiliation: z.enum(['microcenter', 'independent', 'repairdesk']),
   consultationUrl: z.string().optional(),
});
type ProShop = z.infer<typeof ProShopSchema>;

const ProShopListSchema = z.array(ProShopSchema);

type Coordinates = { latitude: number; longitude: number };

type LocationFix = Coordinates & {
   accuracyMeters?: number;
   source: 'browser' | 'query';
};

type LocationStatus = 'idle' | 'locating' | 'located' | 'denied' | 'failed' | 'unavailable';

type PageType = 'Guide' | 'Device' | 'Answers';

type Position = 'Top' | 'Bottom';

type ProLeadSectionProps = {
   deviceName: string;
   guideTitle: string;
   guideid: number;
   wikiid: number;
   questionid: number;
   pageType: PageType;
   position: Position | null;
   guidesAnchorId: string | null;
   stepsAnchorId: string | null;
   imageUrl: string;
   userName: string | null;
   userEmail: string | null;
   recaptchaSiteKey: string | null;
};

export function ProLeadSection({
   deviceName,
   guideTitle,
   guideid,
   wikiid,
   questionid,
   pageType,
   position,
   guidesAnchorId,
   stepsAnchorId,
   imageUrl,
   userName,
   userEmail,
   recaptchaSiteKey,
}: ProLeadSectionProps) {
   const placement = position ? `${pageType} ${position}` : pageType;
   const location = useBrowserLocation();
   const [queryCoords] = useState(debugCoords);
   const coords = location.fix ?? queryCoords;
   const nearestShops = useNearestProShops(coords);
   const shops = useLatestDefined(nearestShops);
   // A location request can still land while the form is open, which can name
   // a different shop mid-form. The lead belongs to the shop the user picked,
   // so hold onto it rather than following the list.
   const [leadFormShop, setLeadFormShop] = useState<ProShop | null>(null);
   // Hold onto the list the user acted on so it can't empty out when
   // coordinates change.
   const [heldShops, setHeldShops] = useState<ProShop[] | null>(null);
   const recaptchaRef = useRef<HTMLDivElement>(null);

   const visibleShops = pickVisibleShops(shops ?? [], heldShops, coords);
   const slot = useProLeadSlot({
      enabled: visibleShops.length > 0,
      guidesAnchorId,
      stepsAnchorId,
   });

   const trackLocationEvent = (action: string, shop: ProShop | undefined) => {
      if (shop) {
         trackShopEvent({ shop, placement, slot: slot.analyticsName, action });
      }
   };
   // The grant is reported against the list the browser's fix produces, so
   // it waits for that list to arrive rather than naming one from the guess.
   const grantToReport = useRef(false);
   useEffect(() => {
      if (grantToReport.current && nearestShops !== null) {
         grantToReport.current = false;
         trackLocationEvent('Location Granted', visibleShops[0]);
      }
   });

   if (visibleShops.length === 0) {
      return null;
   }

   const requestLocation = async (retry: boolean) => {
      trackLocationEvent(retry ? 'Try Again Click' : 'Use My Location Click', visibleShops[0]);
      setHeldShops(visibleShops);
      const outcome = await location.request();
      if (outcome.status === 'located') {
         grantToReport.current = true;
      } else {
         trackLocationEvent(LOCATION_FAILURE_ACTIONS[outcome.reason], visibleShops[0]);
      }
   };

   const card = (
      <>
         <ShopList
            shops={visibleShops}
            imageUrl={imageUrl}
            deviceName={deviceName}
            placement={placement}
            slot={slot.analyticsName}
            locationControl={
               <LocationControl status={location.status} onRequest={requestLocation} />
            }
            onRequestRepair={shop => {
               trackShopEvent({
                  shop,
                  placement,
                  slot: slot.analyticsName,
                  action: 'Find Repair Shop Click',
               });
               setLeadFormShop(shop);
               setHeldShops(visibleShops);
            }}
            onScheduleNow={shop => {
               trackShopEvent({
                  shop,
                  placement,
                  slot: slot.analyticsName,
                  action: 'Schedule Now Click',
               });
            }}
         />
         {leadFormShop && (
            <LeadFormDialog
               shop={remeasure(leadFormShop, coords)}
               deviceName={deviceName}
               guideTitle={guideTitle}
               guideid={guideid}
               wikiid={wikiid}
               questionid={questionid}
               placement={placement}
               slot={slot.analyticsName}
               userName={userName}
               userEmail={userEmail}
               recaptchaSiteKey={recaptchaSiteKey}
               recaptchaRef={recaptchaRef}
               onClose={() => setLeadFormShop(null)}
            />
         )}
         <div ref={recaptchaRef} className="hidden" />
      </>
   );

   return slot.target ? createPortal(card, slot.target) : card;
}

type SlotDecision = {
   target: HTMLElement | null;
   analyticsName: string | undefined;
};

const STAY_PUT: SlotDecision = { target: null, analyticsName: undefined };

function useProLeadSlot({
   enabled,
   guidesAnchorId,
   stepsAnchorId,
}: {
   enabled: boolean;
   guidesAnchorId: string | null;
   stepsAnchorId: string | null;
}): SlotDecision {
   return useMemo(() => {
      if (!enabled) {
         return STAY_PUT;
      }
      const guidesAnchor = guidesAnchorId && document.getElementById(guidesAnchorId);
      if (guidesAnchor) {
         return { target: guidesAnchor, analyticsName: 'After Guides' };
      }
      const stepsAnchor = stepsAnchorId && document.getElementById(stepsAnchorId);
      if (!stepsAnchor) {
         return STAY_PUT;
      }
      const moved = getGrowthbookSync().isOn(GUIDE_POSITION_FLAG);
      return {
         target: moved ? stepsAnchor : null,
         analyticsName: moved ? 'Between Steps' : 'Top',
      };
   }, [enabled, guidesAnchorId, stepsAnchorId]);
}

type LocationFailure = 'denied' | 'unavailable' | 'timeout';

type LocationOutcome =
   | { status: 'located'; fix: LocationFix }
   | { status: 'failed'; reason: LocationFailure };

const LOCATION_FAILURE_ACTIONS: Record<LocationFailure, string> = {
   denied: 'Location Denied',
   unavailable: 'Location Unavailable',
   timeout: 'Location Timed Out',
};

type BrowserLocationState = { status: LocationStatus; fix: LocationFix | null };

/**
 * One answer per page: a guide page mounts this component twice, top and
 * bottom, as separate React roots, and both cards must follow the same fix.
 */
function createBrowserLocationStore() {
   const initial: BrowserLocationState = { status: 'idle', fix: null };
   let state = initial;
   const listeners = new Set<() => void>();

   const set = (next: Partial<BrowserLocationState>) => {
      state = { ...state, ...next };
      listeners.forEach(listener => listener());
   };

   const locate = () =>
      new Promise<LocationOutcome>(resolve => {
         set({ status: 'locating' });
         navigator.geolocation.getCurrentPosition(
            position => {
               const fix: LocationFix = {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  accuracyMeters: position.coords.accuracy,
                  source: 'browser',
               };
               set({ status: 'located', fix });
               resolve({ status: 'located', fix });
            },
            positionError => {
               const reason = failureReason(positionError);
               set({ status: reason === 'denied' ? 'denied' : 'failed' });
               resolve({ status: 'failed', reason });
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 3_600_000 }
         );
      });

   return {
      subscribe: (listener: () => void) => {
         listeners.add(listener);
         return () => listeners.delete(listener);
      },
      get: () => state,
      locate,
      reset: () => set(initial),
   };
}

function failureReason(error: GeolocationPositionError): LocationFailure {
   switch (error.code) {
      case error.PERMISSION_DENIED: {
         return 'denied';
      }
      case error.POSITION_UNAVAILABLE: {
         return 'unavailable';
      }
      default: {
         return 'timeout';
      }
   }
}

export const browserLocationStore = createBrowserLocationStore();

/**
 * The browser only prompts from `request`. When permission was granted on an
 * earlier visit the position is fetched on mount instead, which shows no
 * prompt; browsers without the permissions query start idle.
 */
function useBrowserLocation() {
   const state = useSyncExternalStore(browserLocationStore.subscribe, browserLocationStore.get);
   const available = 'geolocation' in navigator;

   useEffect(() => {
      if (!available) {
         return;
      }
      navigator.permissions
         ?.query({ name: 'geolocation' })
         .then(permission => {
            if (permission.state === 'granted' && browserLocationStore.get().status === 'idle') {
               void browserLocationStore.locate();
            }
         })
         .catch(() => {});
   }, [available]);

   return {
      status: available ? state.status : 'unavailable',
      fix: state.fix,
      request: browserLocationStore.locate,
   };
}

/**
 * Shops further away than the cutoff are left out, except the ones already on
 * screen when the user tapped a shop or the location button, and that held
 * list stands in when nothing clears the cutoff. Both lists were measured
 * from the coordinates we had when they arrived, so measure again before
 * deciding what is in range.
 */
function pickVisibleShops(
   shops: ProShop[],
   heldShops: ProShop[] | null,
   coords: Coordinates | null
): ProShop[] {
   const heldIds = new Set(heldShops?.map(shop => shop.id));
   const visible = nearestFirst(shops, coords).filter(
      shop => shop.distanceMiles <= MAX_DISTANCE_MILES || heldIds.has(shop.id)
   );
   return visible.length > 0 ? visible : nearestFirst(heldShops ?? [], coords);
}

/** Remeasured and back in the order the API sends them, nearest first. */
function nearestFirst(shops: ProShop[], coords: Coordinates | null): ProShop[] {
   return shops
      .map(shop => remeasure(shop, coords))
      .sort((a, b) => a.distanceMiles - b.distanceMiles);
}

/**
 * A shop arrives measured from whatever coordinates fetched it, so its
 * distance goes stale the moment the browser hands us better ones. Shops carry
 * their own coordinates, so measure again rather than wait for the next
 * response to land.
 */
function remeasure(shop: ProShop, coords: Coordinates | null): ProShop {
   if (coords === null) {
      return shop;
   }
   return { ...shop, distanceMiles: distanceMilesBetween(coords, shop) };
}

/** Mirrors GeoUtils::distanceMiles, earth radius included. */
function distanceMilesBetween(from: Coordinates, to: Coordinates): number {
   const earthRadiusMiles = 3959;

   const lat1 = toRadians(from.latitude);
   const lon1 = toRadians(from.longitude);
   const lat2 = toRadians(to.latitude);
   const lon2 = toRadians(to.longitude);

   const deltaLat = lat2 - lat1;
   const deltaLon = lon2 - lon1;

   const a =
      Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;

   return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toRadians(degrees: number): number {
   return (degrees * Math.PI) / 180;
}

type ShopListProps = {
   shops: ProShop[];
   imageUrl: string;
   deviceName: string;
   placement: string;
   slot: string | undefined;
   locationControl: ReactNode;
   onRequestRepair: (shop: ProShop) => void;
   onScheduleNow: (shop: ProShop) => void;
};

function ShopList({
   shops,
   imageUrl,
   deviceName,
   placement,
   slot,
   locationControl,
   onRequestRepair,
   onScheduleNow,
}: ShopListProps) {
   const viewRef = useTrackFirstView<HTMLDivElement>(
      {
         eventCategory: EVENT_CATEGORY,
         eventAction: `${placement} - View`,
         eventName: deviceName,
         ...slotParam(slot),
      },
      { dedupeKey: `${EVENT_CATEGORY} - ${placement} - View` }
   );

   return (
      <div
         ref={viewRef}
         className="my-6 rounded-xl w-full overflow-hidden grid grid-rows-[auto_170px] md:grid-cols-2 md:grid-rows-none"
      >
         <div className="p-4 flex flex-col items-start justify-center gap-4 bg-gray-100 md:py-8">
            <div className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2">
               <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                  <CityTitle city={shops[0].city} />
               </p>
               {locationControl}
            </div>
            <ul className="m-0 flex w-full list-none flex-col gap-2 p-0">
               {shops.map(shop => (
                  <li key={shop.id}>
                     <ShopRow
                        shop={shop}
                        onRequestRepair={() => onRequestRepair(shop)}
                        onScheduleNow={() => onScheduleNow(shop)}
                     />
                  </li>
               ))}
            </ul>
         </div>
         <div className="relative overflow-hidden">
            <img src={imageUrl} alt="" className="absolute inset-0 size-full object-cover" />
         </div>
      </div>
   );
}

function CityTitle({ city }: { city: string }) {
   return (
      <>
         {_jsr(
            'Repair Shops Near %1',
            <strong className="font-semibold text-gray-900">{city}</strong>
         )}
      </>
   );
}

type LocationControlProps = {
   status: LocationStatus;
   onRequest: (retry: boolean) => void;
};

const locationButtonClassName = cn(
   'inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium whitespace-nowrap',
   'text-brand-500 transition-colors hover:bg-brand-50 aria-disabled:text-gray-500 aria-disabled:hover:bg-transparent',
   'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
);

function LocationControl({ status, onRequest }: LocationControlProps) {
   const outcomeRef = useRef<HTMLDivElement>(null);
   const requested = useRef(false);
   const locating = status === 'locating';
   const settled = status === 'located' || status === 'denied';

   // The button goes away on these outcomes, so the reader who pressed it
   // would otherwise lose their place. The move also reads the outcome, so
   // this is not a live region. A fix that arrived on its own moves nothing.
   useEffect(() => {
      if (settled && requested.current) {
         requested.current = false;
         outcomeRef.current?.focus();
      }
   }, [settled]);

   if (status === 'unavailable') {
      return null;
   }

   const request = (retry: boolean) => {
      requested.current = true;
      onRequest(retry);
   };

   return (
      <div
         ref={outcomeRef}
         tabIndex={-1}
         className={cn(
            'flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 outline-none',
            (settled || status === 'failed') && 'basis-full'
         )}
      >
         {status === 'located' && (
            <span className="inline-flex items-center gap-1.5 text-sm text-green-700 whitespace-nowrap">
               <FaIcon name="check" className="size-4" />
               {_js('Using your location')}
            </span>
         )}
         {status === 'denied' && (
            <p className="m-0 text-sm text-gray-600">
               {_js(
                  'Location is off for this site. Turn it on in your browser settings to sort shops by distance from you.'
               )}
            </p>
         )}
         {status === 'failed' && (
            <p role="alert" className="m-0 text-sm text-red-600">
               {_js("We couldn't find your location.")}
            </p>
         )}
         {!settled && (
            <button
               type="button"
               aria-disabled={locating}
               aria-busy={locating}
               onClick={locating ? undefined : () => request(status === 'failed')}
               className={locationButtonClassName}
            >
               {locating ? (
                  <>
                     <FaIcon name="spinner" className="size-4 animate-spin" />
                     {_js('Finding your location...')}
                  </>
               ) : status === 'failed' ? (
                  _js('Try again')
               ) : (
                  <>
                     <FaIcon name="location-crosshairs" className="size-4" />
                     {_js('Use my location')}
                  </>
               )}
            </button>
         )}
      </div>
   );
}

const shopRowClassName = cn(
   'group flex w-full items-center justify-between gap-3 rounded-lg bg-white px-3.5 py-3 text-left',
   'border border-gray-200 transition-colors hover:border-gray-400',
   'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
);

const shopRowIconClassName =
   'size-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-700';

type ShopRowProps = {
   shop: ProShop;
   onRequestRepair: () => void;
   onScheduleNow: () => void;
};

function ShopRow({ shop, onRequestRepair, onScheduleNow }: ShopRowProps) {
   if (shop.affiliation === 'microcenter') {
      return (
         <a
            href={shop.consultationUrl}
            target="_blank"
            rel="noreferrer"
            onClick={onScheduleNow}
            className={shopRowClassName}
         >
            <ShopSummary shop={shop} />
            <FaIcon name="arrow-up-right-from-square" className={shopRowIconClassName} />
         </a>
      );
   }

   return (
      <button type="button" onClick={onRequestRepair} className={shopRowClassName}>
         <ShopSummary shop={shop} />
         <FaIcon name="chevron-right" className={shopRowIconClassName} />
      </button>
   );
}

function streetAddress(shop: ProShop) {
   return shop.addressUnit ? `${shop.address}, ${shop.addressUnit}` : shop.address;
}

function ShopSummary({ shop }: { shop: ProShop }) {
   return (
      <span className="min-w-0">
         <span className="flex flex-wrap items-center gap-x-3">
            <span className="text-base font-semibold text-gray-900">{shop.name}</span>
            <Badge className="badge-brand">
               {_js('%1 mi', Math.round(shop.distanceMiles).toString())}
            </Badge>
         </span>
         <span className="mt-0.5 block text-sm text-gray-600">
            {streetAddress(shop)}, {shop.city}, {shop.state}
         </span>
      </span>
   );
}

type LeadFormDialogProps = {
   shop: ProShop;
   deviceName: string;
   guideTitle: string;
   guideid: number;
   wikiid: number;
   questionid: number;
   placement: string;
   slot: string | undefined;
   userName: string | null;
   userEmail: string | null;
   recaptchaSiteKey: string | null;
   recaptchaRef: RefObject<HTMLDivElement | null>;
   onClose: () => void;
};

function LeadFormDialog({
   shop,
   deviceName,
   guideTitle,
   guideid,
   wikiid,
   questionid,
   placement,
   slot,
   userName,
   userEmail,
   recaptchaSiteKey,
   recaptchaRef,
   onClose,
}: LeadFormDialogProps) {
   const api = useIFixitApiClient();
   const [submitting, setSubmitting] = useState(false);
   const [submitted, setSubmitted] = useState(false);
   const [error, setError] = useState<string | null>(null);

   const handleSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);

      const formData = new FormData(e.currentTarget);

      try {
         let captchaResult: string | undefined;
         if (recaptchaSiteKey) {
            captchaResult = await Recaptcha.executeInvisible(
               recaptchaRef.current,
               recaptchaSiteKey,
               'pro-lead'
            );
         }

         await api.post('lead/submit', 'submit-pro-lead', {
            body: JSON.stringify({
               shopId: shop.id,
               name: formData.get('name'),
               email: formData.get('email'),
               phone: formData.get('phone') || undefined,
               device: formData.get('device') || undefined,
               repair: formData.get('repair') || undefined,
               notes: formData.get('notes') || undefined,
               guideid: guideid || undefined,
               wikiid: wikiid || undefined,
               questionid: questionid || undefined,
               captchaResult,
            }),
         });
         setSubmitted(true);
         trackShopEvent({ shop, placement, slot, action: 'Lead Submitted' });
      } catch {
         setError(_js('Something went wrong. Please try again.'));
      } finally {
         setSubmitting(false);
      }
   };

   return (
      <Dialog
         open
         onOpenChange={open => {
            if (!open) onClose();
         }}
      >
         <DialogContent
            // The form itself is the content, so there is nothing to describe.
            // Radix wants that said out loud instead of pointing at a missing id.
            aria-describedby={undefined}
            className="max-sm:top-safe-top max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-w-full max-sm:h-safe-dvh max-sm:rounded-none max-sm:overflow-y-auto"
         >
            <DialogHeader>
               <DialogTitle>{_js('Request a Repair')}</DialogTitle>
            </DialogHeader>

            {submitted ? (
               <div className="py-8 text-center">
                  <p className="text-lg font-medium text-gray-900 mb-2">{_js('Request sent!')}</p>
                  <p className="text-gray-600">
                     {_js('Your selected shop will contact you soon to discuss the repair.')}
                  </p>
                  <Button onClick={onClose} className="mt-6">
                     {_js('Close')}
                  </Button>
               </div>
            ) : (
               <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="rounded-lg border border-gray-200 p-3">
                     <div className="flex items-center gap-3">
                        <h3 className="text-base font-semibold text-gray-900">{shop.name}</h3>
                        <Badge className="badge-brand">
                           {_js('%1 mi', Math.round(shop.distanceMiles).toString())}
                        </Badge>
                     </div>
                     <p className="text-sm text-gray-600 mt-1">
                        {streetAddress(shop)}, {shop.city}, {shop.state} {shop.postcode}
                     </p>
                  </div>
                  <Field
                     label={_js('Device')}
                     name="device"
                     defaultValue={deviceName || undefined}
                  />
                  <Field
                     label={_js('Repair')}
                     name="repair"
                     defaultValue={guideTitle || undefined}
                  />
                  <div className="flex flex-col gap-1">
                     <label className="text-sm font-medium text-gray-700">{_js('Notes')}</label>
                     <textarea
                        name="notes"
                        rows={3}
                        maxLength={2000}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder={_js('Any additional details about your repair...')}
                     />
                  </div>
                  <Field
                     label={_js('Name')}
                     name="name"
                     defaultValue={userName ?? undefined}
                     required
                  />
                  <Field
                     label={_js('Email')}
                     name="email"
                     type="email"
                     defaultValue={userEmail ?? undefined}
                     required
                  />
                  <Field label={_js('Phone number')} name="phone" type="tel" />

                  {error && <p className="text-sm text-red-600">{error}</p>}

                  <p className="text-gray-500 text-center">
                     {_js('The shop will call or email you directly to discuss the repair.')}
                  </p>

                  <Button type="submit" disabled={submitting} className="mt-2">
                     {submitting ? _js('Submitting...') : _js('Submit Request')}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                     {_js(
                        'By submitting, you agree to our Privacy Policy and consent to receive email and text communications from iFixit and the selected repair shop, as applicable.'
                     )}
                  </p>
               </form>
            )}
         </DialogContent>
      </Dialog>
   );
}

type FieldProps = {
   label: string;
   name: string;
   defaultValue?: string;
   type?: string;
   required?: boolean;
};

function Field({ label, name, defaultValue, type = 'text', required }: FieldProps) {
   return (
      <div className="flex flex-col gap-1">
         <label className="text-sm font-medium text-gray-700">{label}</label>
         <input
            name={name}
            type={type}
            defaultValue={defaultValue}
            required={required}
            className={cn(
               'rounded-lg border border-gray-300 px-3 py-2 text-sm',
               'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            )}
         />
      </div>
   );
}

function useNearestProShops(fix: LocationFix | null) {
   const api = useIFixitApiClient();

   const { data } = useCacheableQuery({
      cacheTime: Duration(1).day,
      schema: ProShopListSchema.nullable(),
      storage: 'localStorage',
      queryKey: [
         'nearest-pro-shops',
         fix?.latitude ?? null,
         fix?.longitude ?? null,
         NEAREST_SHOP_LIMIT,
      ],
      queryFn: async () =>
         api.post<ProShop[] | null>('lead/nearest', 'get-nearest-pro-shops', {
            body: JSON.stringify({
               coordinates: fix ? { latitude: fix.latitude, longitude: fix.longitude } : undefined,
               accuracyMeters: fix?.accuracyMeters,
               coordinatesSource: fix?.source,
               limit: NEAREST_SHOP_LIMIT,
            }),
         }),
   });

   return data ?? null;
}

function debugCoords(): LocationFix | null {
   const searchParams = new URLSearchParams(window.location.search);
   const latitude = Number(searchParams.get('latitude') ?? 'nan');
   const longitude = Number(searchParams.get('longitude') ?? 'nan');
   if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
   return { latitude, longitude, source: 'query' };
}

function trackShopEvent({
   shop,
   placement,
   slot,
   action,
}: {
   shop: ProShop;
   placement: string;
   slot: string | undefined;
   action: string;
}) {
   trackAnalyticsCustomEvent({
      eventCategory: EVENT_CATEGORY,
      eventAction: `${placement} - ${action}`,
      eventName: `${shop.name}, ${shop.city}`,
      ...slotParam(slot),
   });
}

function slotParam(slot: string | undefined) {
   return slot ? { slot } : {};
}

initializeComponent('ProLeadSectionComponent', ProLeadSection);
